export default class TreeNode<T> implements Iterable<TreeNode<T>> {
  value: T
  children: TreeNode<T>[]
  parent?: TreeNode<T>

  constructor(value: T, children: TreeNode<T>[] = [], parent?: TreeNode<T>) {
    this.value = value
    this.children = children
    this.parent = parent
  }

  static of<ST extends { children?: ST[] }>(
    value: ST,
    children: ST[],
    parent?: TreeNode<ST>
  ): TreeNode<ST> {
    let currentNode: TreeNode<ST> = new TreeNode(value, [], parent)

    currentNode = new TreeNode(
      value,
      children.map((child) =>
        TreeNode.of(child, child?.children || [], currentNode)
      ),
      parent
    )

    return currentNode
  }

  *[Symbol.iterator](): Iterator<TreeNode<T>> {
    yield this
    for (const child of this.children) {
      yield* child
    }
  }

  *reverseIterator(): Generator<TreeNode<T>> {
    const queue: TreeNode<T>[] = [this] // Start with the root
    const stack: TreeNode<T>[] = [] // Use a stack to reverse the order

    // Perform BFS to populate the stack
    while (queue.length > 0) {
      const currentNode = queue.shift()

      if (currentNode) {
        stack.push(currentNode)
        // Add children to the queue to continue BFS
        queue.push(...currentNode.children)
      }
    }

    // Yield from the stack to reverse the order
    while (stack.length > 0) {
      const nextItem = stack.pop()

      if (nextItem) {
        yield nextItem
      } else {
        break
      }
    }
  }

  isEmpty(): boolean {
    return !this.value && !this.hasChildren
  }

  unwrap(): T {
    return this.value
  }

  unwrapDeep(includeSelf: boolean): T[] {
    if (!this.children || this.children.length === 0) {
      return includeSelf ? [this.value] : []
    }

    const childrenAccumulator: T[] = []

    for (const child of this.children) {
      childrenAccumulator.push(...child.unwrapDeep(true))
    }

    return includeSelf
      ? [
          {
            ...this.value,
            children: childrenAccumulator,
          },
        ]
      : childrenAccumulator
  }

  map<RV>(f: (value: T) => RV): TreeNode<RV> {
    const parentReference = this.parent as TreeNode<RV> | undefined

    return new TreeNode(
      f(this.value),
      this.children.map((child) => {
        return child.map(f)
      }),
      parentReference
    )
  }

  find(predicate: (value: T) => boolean): TreeNode<T | null> {
    if (predicate(this.value)) {
      return this
    }

    for (const child of this.children) {
      const found = child.find(predicate)

      if (!found.isEmpty()) {
        return found
      }
    }

    return new NullTree()
  }

  filter(predicate: (value: T) => boolean): TreeNode<T | null> {
    const filteredChildren = this.children
      .map((child) => child.filter(predicate))
      .filter((child) => !child.isEmpty()) as TreeNode<T>[]

    if (predicate(this.value)) {
      if (this.children.length === 0 || filteredChildren.length > 0) {
        return new TreeNode(this.value, filteredChildren, this.parent)
      }
    }

    return new NullTree()
  }

  private hasChildren(): boolean {
    return this.children ? this.children.length > 0 : false
  }
}

class NullTree extends TreeNode<null> {
  constructor() {
    super(null, [])
  }

  isEmpty(): boolean {
    return true
  }
}
